import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, AtSign, Globe, Hash } from "lucide-react";

const TYPE_META = {
  address: { icon: AtSign, label: "Adresse email", placeholder: "boss@entreprise.com" },
  domain: { icon: Globe, label: "Domaine", placeholder: "client.com" },
  keyword: { icon: Hash, label: "Mot-clé", placeholder: "contrat, urgent…" },
};

export default function Filters() {
  const [filters, setFilters] = useState([]);
  const [type, setType] = useState("address");
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/filters");
      setFilters(r.data);
    } catch (err) { toast.error("Erreur de chargement"); }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setLoading(true);
    try {
      await api.post("/filters", { type, value: value.trim(), label: label || null });
      toast.success("Filtre ajouté");
      setValue(""); setLabel("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce filtre ?")) return;
    try {
      await api.delete(`/filters/${id}`);
      toast.success("Supprimé");
      await load();
    } catch (err) { toast.error("Erreur"); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="filters-page">
      <div className="mb-8">
        <div className="overline mb-1">// Triage</div>
        <h1 className="text-3xl font-bold tracking-tight mb-2" style={{fontFamily:"'Work Sans', sans-serif"}}>
          Filtres prioritaires
        </h1>
        <p className="text-gray-600">Seuls les emails qui correspondent à au moins un filtre seront marqués prioritaires. Les autres sont automatiquement masqués.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="bg-white border border-gray-200 p-6">
          <div className="overline mb-1">// Nouveau</div>
          <h3 className="text-lg font-semibold mb-4" style={{fontFamily:"'Work Sans', sans-serif"}}>Ajouter un filtre</h3>
          <form onSubmit={add} className="space-y-4" data-testid="filter-form">
            <div>
              <label className="overline block mb-2">Type</label>
              <div className="grid grid-cols-3 border border-gray-300">
                {Object.entries(TYPE_META).map(([key, m]) => {
                  const Icon = m.icon;
                  return (
                    <button key={key} type="button" onClick={() => setType(key)}
                      data-testid={`filter-type-${key}`}
                      className={`flex flex-col items-center gap-1 py-3 text-xs uppercase tracking-wider transition-colors ${
                        type === key ? "bg-[#002FA7] text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}>
                      <Icon className="w-4 h-4" />
                      {key === "address" ? "Email" : key === "domain" ? "Domaine" : "Mot-clé"}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="overline block mb-2">{TYPE_META[type].label}</label>
              <input
                value={value} onChange={(e)=>setValue(e.target.value)}
                data-testid="filter-value-input"
                placeholder={TYPE_META[type].placeholder}
                className="w-full h-11 px-3 border border-gray-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
              />
            </div>
            <div>
              <label className="overline block mb-2">Libellé (optionnel)</label>
              <input
                value={label} onChange={(e)=>setLabel(e.target.value)}
                data-testid="filter-label-input"
                placeholder="Patron, Comptabilité…"
                className="w-full h-11 px-3 border border-gray-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
              />
            </div>
            <button type="submit" disabled={loading} data-testid="filter-add-btn"
              className="w-full h-11 bg-[#002FA7] hover:bg-[#002277] text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Ajouter
            </button>
          </form>
        </div>

        {/* List */}
        <div className="lg:col-span-2 bg-white border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="overline mb-1">// Actifs</div>
              <h3 className="text-lg font-semibold" style={{fontFamily:"'Work Sans', sans-serif"}}>
                {filters.length} filtre{filters.length > 1 ? "s" : ""}
              </h3>
            </div>
          </div>
          {filters.length === 0 ? (
            <div className="p-10 text-center text-gray-500">
              Aucun filtre. Ajoutez-en pour ne voir que les emails qui comptent.
            </div>
          ) : (
            <ul className="divide-y divide-gray-200" data-testid="filters-list">
              {filters.map((f) => {
                const Icon = TYPE_META[f.type]?.icon || Hash;
                return (
                  <li key={f.id} className="p-4 flex items-center gap-4" data-testid={`filter-row-${f.id}`}>
                    <div className="w-10 h-10 bg-gray-50 border border-gray-200 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-gray-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{f.value}</div>
                      <div className="text-xs text-gray-500">
                        <span className="uppercase tracking-wider">{f.type}</span>
                        {f.label && <span> · {f.label}</span>}
                      </div>
                    </div>
                    <button onClick={() => remove(f.id)} data-testid={`filter-delete-${f.id}`}
                      className="p-2 text-red-600 hover:bg-red-50 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
